<template>
  <modal-component :is-displayed="isAssignIcdModalOpened" :no-padding="true" :title="$t('ICDManagement.CreateTitle')"
    @toggle-modal="$emit('toggle-modal')">

    <new-customer-form v-if="isNewCustomerModalOpened" 
      :is-new-customer-modal-opened="isNewCustomerModalOpened"
      @toggle-modal="isNewCustomerModalOpened = false" 
      @get-customer="getNewCustomer" 
      :refresh-data="getCustomers" />

    <form-wrapper :is-form-in-modal="true">
      <form @submit.prevent="handleSubmit">
        <form-divider>
          <div class="relative items-center py-4">
            <label class="whitespace-nowrap text-sm font-medium not-italic text-gray-800">{{
              $t("ICDManagement.CreateLabelTag")
            }}</label>
            <div class="flex items-center justify-center gap-2">
              <small-loader v-if="isCustomersListLoading" height="h-8" width="w-8" />
              <v-select v-else v-model="customer" :class="`mt-1 w-full`" :options="Customers" label="name" />
              <div class="pt-1">
                <the-button :icon="['fas', 'add']" type="button" :disabled="false"
                  @execute-method="toggleNewCustomerModal" />
              </div>
            </div>
          </div>
        </form-divider>
        <button-wrapper :is-submit-loading="isSubmitLoading" />
      </form>
    </form-wrapper>
  </modal-component>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import ModalComponent from "@/core/Components/Modal.vue";
import FormWrapper from "@/core/Components/FormElements/FormWrapper.vue";
import FormDivider from "@/core/Components/FormElements/FormDivider.vue";
import SmallLoader from "@/core/Components/SmallLoader.vue";
import VSelect from "vue-select";
import ButtonWrapper from "@/core/Components/FormElements/ButtonWrapper.vue";
import TheButton from "@/core/Components/TheButton.vue";
import InputField from "@/core/Components/FormElements/InputField.vue";
import { useToast } from "vue-toastification";
import { ErrorMessageFormatter, isString } from "@/core/Services/GlobalFunctions";
import useValidate from "@vuelidate/core";
import TextBoxMask from "@/core/Components/FormElements/TextBoxMask.vue";
import "devextreme/dist/css/dx.light.css";
import { DxTextBox } from "devextreme-vue/text-box";
import { AssignIcd, ICDCustomer, ICDNewCustomer } from "../../Services/ICDManagementTypes";
import IcdService from "../../Services/ICDManagementService";
import NewCustomerForm from "../AdditionalModals/NewCustomerForm.vue";

export default defineComponent({
  components: {
    ButtonWrapper,
    VSelect,
    SmallLoader,
    FormDivider,
    FormWrapper,
    ModalComponent,
    TextBoxMask,
    DxTextBox,
    TheButton,
    InputField,
    NewCustomerForm
  },
  props: {
    isAssignIcdModalOpened: Boolean,
    sensorMac: {
      type: String,
      required: true,
    }
  },
  emits: ["toggle-modal", "refresh-icd-report"],
  data() {
    return {
      toast: useToast(),
      v$: useValidate(),
      icdService: new IcdService(),
      isSubmitLoading: false,
      Customers: [] as ICDCustomer[],
      customer: {
        id: null,
        name: ""
      } as ICDCustomer,
      isCustomersListLoading: false,
      isDataSourceLoading: false,
      icd: {
        mac: "",
        customerId: null,
      } as AssignIcd,
      isNewCustomerModalOpened: false
    };
  },
  async created() {
    try {
      await this.getCustomers();
    } catch (error: any) {
      if (!isString(error.response.data)) {
        this.toast.error(ErrorMessageFormatter(error));
        return;
      }
      this.toast.error(error.response.data);
    }
  },
  methods: {
    async getCustomers() {
      this.isDataSourceLoading = true;
      try {
        this.Customers = await this.icdService.getCustomers({
          pageNumber: 1,
          pageSize: 100000,
        });
      } catch (error: any) {
        if (!isString(error.response.data)) {
          this.toast.error(ErrorMessageFormatter(error));
          return;
        }
        this.toast.error(error.response.data);
      } finally {
        this.isDataSourceLoading = false;
      }
    },
    async handleSubmit() {
      this.isSubmitLoading = true;
      try {
        if (this.sensorMac == "" || this.sensorMac == undefined) {
          this.toast.warning("Something gone wrong. Contact your Administrator!");
          return;
        }
        if (this.customer.id == null) {
          this.toast.warning("You should choose tag name");
          return;
        }
        this.icd = {
          mac: this.sensorMac,
          customerId: this.customer.id,
        };

        await this.icdService.assignIcd(this.icd);
        this.$emit("refresh-icd-report");
        this.$emit("toggle-modal");
        this.toast.success("ICD Assigned successfully!");
      } catch (error: any) {
        if (!isString(error.response.data)) {
          this.toast.error(ErrorMessageFormatter(error));
          return;
        }
        this.toast.error(error.response.data);
      } finally {
        this.isSubmitLoading = false;
      }
    },

    async toggleNewCustomerModal() {
      this.isNewCustomerModalOpened = !this.isNewCustomerModalOpened;
    },
    async getNewCustomer(customer: ICDNewCustomer) {
      this.customer = {
        id: customer.data,
        name: customer.name,
      };
      await this.getCustomers();
    },
  },
});
</script>

<style scoped></style>
